import { components } from "@/api-schema";
import { renderAttributeAppRole } from "@/common/helpers/render-attribute-app-role";
import { DeviceType } from "@/common/hooks/use-device";
import { ImplementedAccessorColumnDef } from "@/components/data-table/types";
import { RelationLink } from "@/components/relation-link/relation-link";
import { BadgeColorVariant, BadgeVariant } from "@/components/ui/badge";
import { EmptyValue } from "@/components/ui/empty-value";
import { ExpandableBadgeList } from "@/components/ui/expandable-badge-list";
import i18next from "i18next";

export type UserAppRoleRow = components["schemas"]["AppRole"];
type Group = components["schemas"]["Group"];

export function getActiveGroups(groups: Group[] | undefined): Group[] {
  return groups?.filter((group) => !group.deletedAt) ?? [];
}

export const userAppRoleTableDefaultColumns = {
  columnAppRoleName: {
    id: "name",
    size: 600,
    enableSorting: true,
    header: () => i18next.t("ram:AppRoles"),
    accessorFn: (row: UserAppRoleRow) => renderAttributeAppRole(row),
    cell: (row) => {
      const name = row.getValue() as string;
      return <span className="font-medium">{name}</span>;
    },
  } as ImplementedAccessorColumnDef<UserAppRoleRow, string>,

  columnApplication: {
    id: "application",
    size: 400,
    enableSorting: true,
    header: () => i18next.t("ram:Application"),
    visibility: [DeviceType.Tablet, DeviceType.Desktop, DeviceType.Widescreen],
    accessorFn: (row: UserAppRoleRow) => row.application?.name ?? "",
    cell: (row) => {
      const application = row.row.original.application;
      if (!application?.name) {
        return <EmptyValue />;
      }
      return (
        <RelationLink subject="Application" uuid={application.uuid}>
          {application.name}
        </RelationLink>
      );
    },
  } as ImplementedAccessorColumnDef<UserAppRoleRow, string>,

  columnGroups: {
    id: "groups",
    size: 500,
    enableSorting: false,
    header: () => i18next.t("ram:Groups"),
    visibility: [DeviceType.Desktop, DeviceType.Widescreen],
    accessorFn: (row: UserAppRoleRow) => getActiveGroups(row.groups),
    cell: (row) => {
      const groups = row.getValue() as components["schemas"]["Group"][];
      if (!groups || groups.length === 0) {
        return <EmptyValue />;
      }
      return (
        <ExpandableBadgeList
          defaultVisible={3}
          items={groups.map((group) => ({
            key: group.uuid,
            label: (
              <RelationLink subject="Group" uuid={group.uuid}>
                {group.name}
              </RelationLink>
            ),
            variant: BadgeVariant.Filled,
            colorVariant: BadgeColorVariant.Secondary,
          }))}
        />
      );
    },
  } as ImplementedAccessorColumnDef<UserAppRoleRow, components["schemas"]["Group"][]>,
};
